import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { countryCodes, defaultCountryCode, parsePhoneNumber, formatPhoneWithCode } from '@/data/countryCodes';

export default function PhoneInputAdmin({ 
  value, 
  onChange, 
  placeholder = "Phone number",
  required = false,
  testId = "phone-input"
}) {
  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Parse initial value
  useEffect(() => {
    if (value) {
      const parsed = parsePhoneNumber(value);
      setCountryCode(parsed.countryCode);
      setPhoneNumber(parsed.number);
    }
  }, []);

  // Update parent when values change
  const handleCountryChange = (newCode) => {
    setCountryCode(newCode);
    if (phoneNumber) {
      onChange(formatPhoneWithCode(newCode, phoneNumber));
    }
  };

  const handlePhoneChange = (e) => {
    const newNumber = e.target.value.replace(/[^\d\s-]/g, '');
    setPhoneNumber(newNumber);
    onChange(formatPhoneWithCode(countryCode, newNumber));
  };

  // Filter countries based on search
  const filteredCountries = searchQuery
    ? countryCodes.filter(c => 
        c.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.code.includes(searchQuery)
      )
    : countryCodes;

  const selectedCountry = countryCodes.find(c => c.code === countryCode) || countryCodes[0];

  return (
    <div className="flex gap-1">
      <Select value={countryCode} onValueChange={handleCountryChange}>
        <SelectTrigger className="w-[90px] h-10" data-testid={`${testId}-country`}>
          <SelectValue>
            <span className="flex items-center gap-1">
              <span className="text-sm">{selectedCountry.flag}</span>
              <span className="text-xs">{selectedCountry.code}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[250px]">
          <div className="p-2 sticky top-0 bg-white border-b z-10">
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-7 text-sm"
              data-testid={`${testId}-search`}
            />
          </div>
          {filteredCountries.map((country) => (
            <SelectItem 
              key={country.iso} 
              value={country.code}
              className="cursor-pointer text-sm"
            >
              <span className="flex items-center gap-1">
                <span>{country.flag}</span>
                <span className="truncate max-w-[100px]">{country.country}</span>
                <span className="text-xs text-gray-500">{country.code}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="tel"
        value={phoneNumber}
        onChange={handlePhoneChange}
        placeholder={placeholder}
        required={required}
        className="flex-1 h-10"
        data-testid={`${testId}-number`}
      />
    </div>
  );
}
